import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, TriangleAlert } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useMotivosDeEncerramento from "../../hooks/useMotivosDeEncerramento";
import EscolherMotivoDeEncerramento from "../EscolherMotivoDeEncerramento";
import EscolhaDoAlvo from "./EscolhaDoAlvo";

// Encerrar a lista inteira de uma vez.
//
// Antes era um "tem certeza?" e nada mais: fechava o que estava em
// atendimento, deixava os aguardando para trás e não gravava motivo nenhum.
// Aqui a pessoa escolhe o que alcança, vê quantos são e diz por quê — o mesmo
// motivo que o encerramento de um já pedia.
// `ticketIds` vazio = a lista inteira, pelo alvo escolhido. Com ids, é a
// seleção da pessoa: a escolha do alvo sai da tela, porque ela já respondeu
// conversa por conversa qual queria.
const ResolverTodosModal = ({ open, onClose, aoConcluir, ticketIds }) => {
	const porSelecao = Boolean(ticketIds?.length);
	const [alvo, setAlvo] = useState("ambos");
	const { cadastrados: MOTIVOS } = useMotivosDeEncerramento();
	const [motivo, setMotivo] = useState(null);
	// Mesma regra do encerramento de um: sem escolha, o primeiro da lista.
	const motivoEscolhido = motivo ?? MOTIVOS[0]?.chave ?? null;
	const [observacao, setObservacao] = useState("");
	const [contagens, setContagens] = useState(null);
	const [carregando, setCarregando] = useState(false);
	const [salvando, setSalvando] = useState(false);

	useEffect(() => {
		if (!open) return;

		setAlvo("ambos");
		setObservacao("");
		if (porSelecao) return;

		setCarregando(true);

		(async () => {
			try {
				const { data } = await api.get("/tickets/bulk-preview");
				setContagens(data);
			} catch {
				// Sem a prévia o modal ainda funciona: o backend é quem decide o
				// alcance de verdade. Zerar a contagem seria mentir.
				setContagens(null);
			}
			setCarregando(false);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, porSelecao]);

	const quantos = porSelecao
		? ticketIds.length
		: alvo === "ambos"
			? (contagens?.open || 0) + (contagens?.pending || 0)
			: contagens?.[alvo] || 0;

	const encerrar = async () => {
		setSalvando(true);
		try {
			const { data } = await api.put("/tickets/close-all", {
				alvo,
				ticketIds: porSelecao ? ticketIds : undefined,
				closeReason: motivoEscolhido || undefined,
				closeNote: observacao.trim() || undefined,
			});

			toast.success(
				data?.encerrados
					? `${data.encerrados} atendimento(s) encerrado(s).`
					: "Nenhum atendimento para encerrar.",
			);

			// Falha por item, não um "deu erro" genérico: com trinta na fila,
			// saber que dois não fecharam é diferente de achar que nenhum fechou.
			if (data?.falhas) {
				toast.warning(
					`${data.falhas} atendimento(s) não puderam ser encerrados.`,
				);
			}

			if (aoConcluir) aoConcluir(data);
			onClose();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						{porSelecao
							? "Encerrar os atendimentos selecionados"
							: "Encerrar vários atendimentos"}
					</DialogTitle>
					<DialogDescription>
						As mensagens continuam salvas. O que for encerrado passa para
						Resolvidos.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					{porSelecao ? (
						<p className="rounded-lg border bg-muted/40 p-2.5 text-sm">
							<strong className="tabular-nums">{ticketIds.length}</strong>{" "}
							conversa{ticketIds.length > 1 ? "s" : ""} selecionada
							{ticketIds.length > 1 ? "s" : ""} na lista.
						</p>
					) : (
						<EscolhaDoAlvo
							valor={alvo}
							aoMudar={setAlvo}
							contagens={contagens}
							carregando={carregando}
						/>
					)}

					<div className="space-y-1.5">
						<EscolherMotivoDeEncerramento
							motivos={MOTIVOS}
							valor={motivoEscolhido}
							onChange={setMotivo}
						/>
						<p className="text-xs text-muted-foreground">
							O mesmo motivo vale para todos deste lote, e fica registrado na
							conversa de cada um.
						</p>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="closeNoteLote">Observação (opcional)</Label>
						<Textarea
							id="closeNoteLote"
							rows={2}
							value={observacao}
							onChange={(e) => setObservacao(e.target.value)}
							placeholder="Ex.: limpeza de fim de expediente."
						/>
					</div>

					{quantos > 20 && (
						<p className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
							<TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
							São {quantos} atendimentos. Se a conexão pede nota ao encerrar,
							cada cliente vai receber a pergunta.
						</p>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={salvando}>
						Cancelar
					</Button>
					<Button onClick={encerrar} disabled={salvando || !quantos}>
						{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
						{quantos ? `Encerrar ${quantos}` : "Nada para encerrar"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default ResolverTodosModal;
